import { Box } from '@chakra-ui/react';
import { sandboxDataCopy } from '@/copy';
import { isSandboxDataNotice } from '@/lib/sandboxData';

/**
 * Mode-invariant token pair (D70 FE6): the existing warning amber with the
 * existing near-black gray, about 7.8:1. Neither has a dark-mode override, so
 * the pair is identical in light and dark; a unit test parses both from
 * `variables.css` and pins the contrast.
 */
export const SANDBOX_INDICATOR_TOKENS = {
  background: '--color-warning',
  text: '--color-gray-dark',
} as const;

/** Above Chakra's `modal` (1400), below `toast` (1700); a unit test pins the band. */
export const SANDBOX_INDICATOR_Z_INDEX = 1650;

/**
 * Persistent staging marker. An overlay, not a strip: fixed to the top edge at
 * the safe-area inset and `pointer-events: none`, so no existing offset moves
 * and nothing underneath loses a tap. Mount ONCE at the app root, outside the
 * router and any layout, before the app so it is first in DOM order. It is a
 * plain element (not `role="status"`, not focusable, never aria-hidden): the
 * visible text is its name. Opts into view transitions via `data-app-chrome`
 * (own name and static-group rules in `variables.css`) so it does not slide
 * with page transitions.
 */
export function SandboxDataIndicator() {
  if (!isSandboxDataNotice()) return null;

  return (
    <Box
      data-testid="sandbox-data-indicator"
      data-app-chrome="sandbox-indicator"
      position="fixed"
      top="env(safe-area-inset-top, 0px)"
      left="50%"
      transform="translateX(-50%)"
      maxW="calc(100vw - 32px)"
      h="16px"
      lineHeight="16px"
      px={2}
      fontSize="xs"
      fontWeight="semibold"
      whiteSpace="nowrap"
      overflow="hidden"
      textOverflow="ellipsis"
      borderBottomRadius="4px"
      pointerEvents="none"
      zIndex={SANDBOX_INDICATOR_Z_INDEX}
      bg={`var(${SANDBOX_INDICATOR_TOKENS.background})`}
      color={`var(${SANDBOX_INDICATOR_TOKENS.text})`}
    >
      {sandboxDataCopy.indicator}
    </Box>
  );
}
